import { css } from 'aphrodite';
import * as React from 'react';
import { findUnresolvableAutoAcknowledgementVariables } from '../../../models/Support/savedReplySubstitution';
import { useSavedRepliesQuery } from '../../../queries/Support/useSupportSavedReplies';
import { Button } from '../../../web/components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { autoAcknowledgementFieldsStyles as styles } from './styles/autoAcknowledgementFieldsStyles';
import { settingsStyles as st } from './styles/settingsStyles';

interface IAutoAcknowledgementFieldsProps {
	mailboxId: string;
	isEnabled: boolean;
	savedReplyId: string;
	onChange: (next: { isEnabled: boolean; savedReplyId: string }) => void;
}

export function AutoAcknowledgementFields({
	mailboxId,
	isEnabled,
	savedReplyId,
	onChange,
}: IAutoAcknowledgementFieldsProps) {
	const { data, isLoading } = useSavedRepliesQuery(mailboxId);
	const replies = data?.values ?? [];
	const selectedReply = replies.find(reply => reply.id === savedReplyId);
	const droppedVariables = selectedReply ? findUnresolvableAutoAcknowledgementVariables(selectedReply.body) : [];

	return (
		<>
			<div className={css(st.toggleRow, styles.settingRow)}>
				<Button
					type='button'
					variant='ghost'
					styles={[st.toggle, isEnabled && st.toggleActive]}
					onClick={() => onChange({ isEnabled: !isEnabled, savedReplyId })}
					aria-pressed={isEnabled}
					aria-label='Auto-reply on new tickets'
				>
					<span className={css(st.toggleKnob, isEnabled && st.toggleKnobActive)} />
				</Button>
				<div className={css(st.toggleText)}>
					<span className={css(st.toggleTitle, styles.settingTitle)}>Auto-reply on new tickets</span>
					<span className={css(st.toggleDescription)}>
						Sends a saved reply the moment a customer emails this mailbox, so they know it arrived. Replies on an
						existing ticket are never auto-answered.
					</span>
				</div>
			</div>

			{isEnabled && (
				<div className={css(styles.nested)}>
					<div className={css(st.field)}>
						<label className={css(st.fieldLabel)}>Reply to send</label>
						<Select
							value={savedReplyId}
							onValueChange={value => onChange({ isEnabled, savedReplyId: value })}
							disabled={isLoading || replies.length === 0}
						>
							<SelectTrigger>
								{/* Base UI renders the raw value unless given children, which would show the reply's id. */}
								<SelectValue placeholder={replies.length === 0 ? 'No saved replies yet' : 'Choose a saved reply…'}>
									{selectedReply?.name ?? 'Reply no longer available'}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								{replies.map(reply => (
									<SelectItem key={reply.id} value={reply.id}>
										{reply.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<div className={css(st.hint)}>
							Edit the wording — including any response-time promise — on the Saved Replies tab. Changes apply to the
							next ticket.
						</div>
					</div>

					{!isLoading && !savedReplyId && (
						<div className={css(styles.warning)}>
							{replies.length === 0
								? 'Create a saved reply first, then choose it here. The auto-reply stays off until one is selected.'
								: 'Choose a reply — the auto-reply stays off until you do.'}
						</div>
					)}

					{!isLoading && !!savedReplyId && !selectedReply && (
						<div className={css(styles.warning)}>
							The reply this mailbox is set to send is not among the ones loaded here — it may have been deleted, or you
							may not have access to it. It is still what gets sent until you pick a different one, or turn the
							auto-reply off.
						</div>
					)}

					{droppedVariables.length > 0 && (
						<div className={css(styles.warning)}>
							{`“${selectedReply.name}” uses ${droppedVariables.join(', ')}, which ${droppedVariables.length === 1 ? 'is' : 'are'} not known when a ticket first arrives — ${droppedVariables.length === 1 ? 'it' : 'they'} will be left out. Give a fallback like {agent_name|our team} to fill the gap.`}
						</div>
					)}
				</div>
			)}
		</>
	);
}
